import { inject, Injectable } from '@angular/core'
import { SocketStockEvEnum } from '@modules/prices-module/socket-prices/enums/socket-stock-ev.enum'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { delay, map, Observable, of, Subject } from 'rxjs'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { SocketStocksResponseType } from '@modules/prices-module/socket-prices/types/socket-stocks-response.type'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { SocketStocksRequestInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stocks-request.interface'
import { SocketStocksActionType } from '@modules/prices-module/socket-prices/types/socket-stocks-action.type'
import { UtilsService } from '@shared/services/utils.service'
import { SocketPricesHelperService } from '@modules/prices-module/socket-prices/services/socket-prices-helper.service'
import { ParamsSubsTickersType } from '@modules/prices-module/socket-prices/types/params-subs-tickers.type'

@Injectable({
	providedIn: 'root',
})
export class SocketPricesOptionsService {
	protected readonly map: MapConstructor = Map
	protected readonly socketStockEvEnum: typeof SocketStockEvEnum = SocketStockEvEnum

	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _websocketService: WebsocketService<SocketStocksRequestInterface, SocketStocksResponseType[]> =
		inject<WebsocketService<SocketStocksRequestInterface, SocketStocksResponseType[]>>(WebsocketService)
	private readonly _socketPricesHelperService: SocketPricesHelperService =
		inject<SocketPricesHelperService>(SocketPricesHelperService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	private _subsTickers: Map<string, string[]> = new this.map<string, string[]>() // ticker: entities[]

	private _resubscribeOptions$: Subject<void> = new Subject<void>()

	get socketUrl(): string {
		return `${this._environmentsHelperService.socketOptionsUrl}/ws/options/`
	}

	get resubscribeOptions$(): Observable<void> {
		return this._resubscribeOptions$.asObservable().pipe(delay(1000))
	}

	private _formatOptionForSocket(option: string): string {
		return `${this.socketStockEvEnum.Q}.${option}, ${this.socketStockEvEnum.T}.${option}`
	}

	private _sendTickersToSocket(tickers: string[], action: SocketStocksActionType): void {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		const deviceId: string | null = this._userHelperService.deviceId
		if (!userDetail || !deviceId) return

		const formattedTickers: string = tickers
			.map((option: string): string => this._formatOptionForSocket(option))
			.join(', ')

		this._websocketService.sendMessage(this.socketUrl, {
			action,
			tickers: !tickers.length ? '' : formattedTickers,
			request_id: this._utilsService.generateUniqId(),
			user_id: userDetail.id,
			device_id: deviceId,
		})
	}

	public setResubscribeOptions(): void {
		this._resubscribeOptions$.next()
	}

	public toggleSubTickers(params: ParamsSubsTickersType): void {
		if (params.destroyAll) {
			this._subsTickers.clear()
			this._sendTickersToSocket([], 'unsubscribe')
			return
		}

		const { tickers, action, entity } = params
		if (!tickers.length) return

		const targetTickers: string[] = []

		tickers.forEach((ticker: string): void => {
			const isNewTicker: boolean = !this._subsTickers.has(ticker)

			switch (action) {
				case 'subscribe': {
					if (isNewTicker) targetTickers.push(ticker)

					const existEntities: string[] = this._subsTickers.get(ticker) ?? []
					const resEntities: string[] = [...existEntities, entity]

					this._subsTickers.set(ticker, resEntities)

					break
				}
				case 'unsubscribe': {
					const existEntities: string[] = this._subsTickers.get(ticker) ?? []
					const resEntities: string[] = existEntities.filter((e: string): boolean => e !== entity)
					if (!isNewTicker && !resEntities.length) {
						targetTickers.push(ticker)
						this._subsTickers.delete(ticker)
					} else {
						this._subsTickers.set(ticker, resEntities)
					}

					break
				}
			}
		})

		if (targetTickers.length) {
			this._sendTickersToSocket(targetTickers, action)
		}
	}

	public openSocketOptions(): Observable<SocketStocksType[]> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of([])

		this._websocketService.connect(this.socketUrl)

		return this._websocketService
			.getResponses(this.socketUrl)
			.pipe(
				map((response: SocketStocksResponseType[]): SocketStocksType[] =>
					this._socketPricesHelperService.formatSocketStocks(response),
				),
			)
	}
}
